import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Button from "../../Common/FormElements/Button/Button";
import {
  fetchIaCorpusStatus,
  fetchIaDocuments,
  reindexIa,
  uploadIaDocument,
} from "../../../REDUX/actions/iaAdmin";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import IaDeleteDocumentModal from "./IaDeleteDocumentModal";

const STATUS_CLASS = {
  indexed: "iaBadgeOk",
  pending: "iaBadgeWait",
  indexing: "iaBadgeWait",
  failed: "iaBadgeKo",
  skipped: "iaBadgeMuted",
};

// En dessous, le PDF n'a quasiment pas de couche texte : c'est un scan.
const SCAN_THRESHOLD = 200;

const formatSize = (bytes) => {
  if (!bytes) return "-";
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${Math.round(bytes / 1024)} Ko`;
};

const IaAdminDocuments = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const fileInputRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const { documents, documentsLoading, uploading, reindexing, corpusStatus } =
    useSelector((state) => state.iaAdminReducer);

  useEffect(() => {
    dispatch(fetchIaDocuments());
    dispatch(fetchIaCorpusStatus());
  }, [dispatch]);

  const filtered = documents.filter((document) => {
    if (statusFilter !== "all" && document.status !== statusFilter) return false;
    if (sourceFilter !== "all" && document.sourceType !== sourceFilter) return false;
    return true;
  });

  return (
    <div className="d-flex flex-column gap-4">
      {/* --------------------------- Synthese --------------------------- */}
      {corpusStatus && (
        <div className="d-flex flex-wrap gap-3">
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Indexed documents")}</p>
            <p className="f20 sBold iaBlue mb-0">{corpusStatus.counts?.indexed || 0}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Pending")}</p>
            <p className="f20 sBold iaBlue mb-0">{corpusStatus.counts?.pending || 0}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Failed")}</p>
            <p className="f20 sBold text-danger mb-0">{corpusStatus.counts?.failed || 0}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Probably scanned")}</p>
            <p className="f20 sBold text-warning mb-0">{corpusStatus.likelyScanned || 0}</p>
          </div>
          {corpusStatus.storage && !corpusStatus.storage.error && (
            <div className="iaStatCard br5 bg-lightGrey p-3">
              <p className="f12 normal text-lightBlack mb-1">{t("Storage cost / day")}</p>
              <p className="f20 sBold iaBlue mb-0">
                ${(corpusStatus.storage.estimatedCostPerDayUsd || 0).toFixed(3)}
              </p>
              <p className="f11 normal text-lightBlack mb-0">
                {formatSize(corpusStatus.storage.usageBytes)} ({t("first GB free")})
              </p>
            </div>
          )}
        </div>
      )}

      {corpusStatus?.likelyScanned > 0 && (
        <div className="iaWarning br5 p-3">
          <p className="f13 normal mb-0">
            {t(
              "Some PDFs contain almost no text: they are scans without a text layer and the assistant cannot use them. Sort by extracted characters to identify them."
            )}
          </p>
        </div>
      )}

      {/* ----------------------------- Actions ---------------------------- */}
      <div className="alignH gap-3 flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv"
          className="d-none"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) dispatch(uploadIaDocument(file, file.name, t));
            event.target.value = "";
          }}
        />

        <Button loading={uploading} onClick={() => fileInputRef.current?.click()}>
          {t("Add a document")}
        </Button>

        <Button
          variant="outline"
          loading={reindexing}
          onClick={() => dispatch(reindexIa(null, t))}
        >
          {t("Reindex everything")}
        </Button>

        <select
          className="br5 p-2 b-1-darkGrey f13"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="all">{t("All statuses")}</option>
          <option value="indexed">{t("Indexed")}</option>
          <option value="pending">{t("Pending")}</option>
          <option value="failed">{t("Failed")}</option>
          <option value="skipped">{t("Skipped")}</option>
        </select>

        <select
          className="br5 p-2 b-1-darkGrey f13"
          value={sourceFilter}
          onChange={(event) => setSourceFilter(event.target.value)}
        >
          <option value="all">{t("All sources")}</option>
          <option value="guide">{t("Guide")}</option>
          <option value="product">{t("Product")}</option>
          <option value="manual">{t("Manual upload")}</option>
        </select>
      </div>

      <p className="f12 normal text-lightBlack mb-0">
        {t(
          "Accepted formats: PDF, Excel (.xlsx, .xls) and CSV. Spreadsheets are converted to text row by row before indexing; the original file remains downloadable."
        )}
      </p>

      {/* ----------------------------- Tableau ---------------------------- */}
      {documentsLoading ? (
        <span className="f14 normal text-lightBlack">{t("Loading")}...</span>
      ) : (
        <div className="table-responsive">
          <table className="iaTable w100 f13">
            <thead>
              <tr>
                <th>{t("Title")}</th>
                <th>{t("Source")}</th>
                <th>{t("Size")}</th>
                <th>{t("Status")}</th>
                <th>{t("Extracted characters")}</th>
                <th>{t("Indexed on")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const scanned =
                  a.status === "indexed" &&
                  typeof a.extractedChars === "number" &&
                  a.extractedChars < SCAN_THRESHOLD;

                return (
                  <tr key={a.id}>
                    <td>
                      <span className="text-lightBlack">{a.title || a.fileName}</span>
                      {a.errorMessage && (
                        <p className="f11 normal text-danger mb-0">{a.errorMessage}</p>
                      )}
                    </td>
                    <td>{t(a.sourceType === "manual" ? "Manual upload" : a.sourceType === "product" ? "Product" : "Guide")}</td>
                    <td>{formatSize(a.fileSize)}</td>
                    <td>
                      <span className={`iaBadge ${STATUS_CLASS[a.status] || ""}`}>
                        {t(a.status)}
                      </span>
                      {a.excluded && (
                        <span className="iaBadge iaBadgeMuted ms-1">{t("Excluded")}</span>
                      )}
                    </td>
                    <td className={scanned ? "text-warning sBold" : ""}>
                      {typeof a.extractedChars === "number" ? a.extractedChars : "-"}
                    </td>
                    <td>{a.indexedAt ? moment(a.indexedAt).format("DD/MM/YYYY HH:mm") : "-"}</td>
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="border-0 bg-transparent f12 iaBlue"
                        onClick={() => dispatch(reindexIa(a.id, t))}
                      >
                        {t("Reindex")}
                      </button>
                      <button
                        type="button"
                        className="border-0 bg-transparent f12 text-danger"
                        onClick={() =>
                          dispatch(
                            openCenteredModal(<IaDeleteDocumentModal document={a} />)
                          )
                        }
                      >
                        {t("Delete")}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!filtered.length && (
                <tr>
                  <td colSpan={7} className="text-center text-lightBlack py-3">
                    {t("No document")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IaAdminDocuments;
